import {
  createElement,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  ReactNode,
  useState,
} from 'react';

/**
 * A configuration that defines how content component content can be filtered.
 */
export interface ContentFilterConfig {
  /**
   * This value will be passed directly into the sas-drive service as a uri param. It is expected to
   * be in the standard SAS microservice filter expression syntax, and will be passed directly to the
   * underlying service.
   */
  queryModeFilter?: string;
}

export type ContentFilterContextValue = ContentFilterConfig | undefined;

export const ContentFilterContext = createContext<ContentFilterContextValue>(undefined);
export const ContentFilterDispatch = createContext<
  Dispatch<SetStateAction<ContentFilterContextValue>> | undefined
>(undefined);

/**
 * Returns the current filter context for the nearest provider, or undefined if not under a
 * ContentFilterProvider
 */
export const useContentFilter = () => useContext(ContentFilterContext);

/**
 *
 * @returns Dispatch a new filter config to all components consuming this provider. Will return
 * undefined if not under a ContentFilterProvider.
 */
export const useContentFilterDispatch = () => useContext(ContentFilterDispatch);

export interface ContentFilterProviderProps {
  initialValue?: ContentFilterContextValue;
  children?: ReactNode;
}

/**
 * Provide content filter configuration to components that consume this provider. Content components
 * that support filtering will use this filtering configuration if available, but it will be
 * superseded by any component or hook config level filter configurations.
 * @param initialValue
 * @param children
 */
export const ContentFilterProvider = ({ initialValue, children }: ContentFilterProviderProps) => {
  const [state, setState] = useState(initialValue);
  return (
    <ContentFilterContext.Provider value={state}>
      <ContentFilterDispatch.Provider value={setState}>{children}</ContentFilterDispatch.Provider>
    </ContentFilterContext.Provider>
  );
};
